import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, LogOut, Calendar, Users as UsersIcon, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API, setToken, clearToken, getToken } from '../admin/api';
import TodayView from '../admin/TodayView';
import CustomersView from '../admin/CustomersView';
import AnalyticsView from '../admin/AnalyticsView';

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!getToken());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate stored token on mount/login change. axios/API/clearToken are stable module values.
  useEffect(() => {
    if (!isLoggedIn) return;
    axios
      .get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .catch((err) => {
        if (err?.response?.status === 401) {
          clearToken();
          setIsLoggedIn(false);
        }
      });
  }, [isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      if (data?.success && data?.token) {
        setToken(data.token);
        setIsLoggedIn(true);
      }
    } catch {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearToken(); setIsLoggedIn(false); setPassword(''); };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Mike&apos;s Admin</h1>
              <p className="text-gray-600">Mini Mike Mowing</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <Input data-testid="login-username" type="text" value={username}
                  onChange={(e) => setUsername(e.target.value)} placeholder="admin" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Input data-testid="login-password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="pr-10" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" data-testid="toggle-password">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="login-error">{error}</div>}
              <Button data-testid="login-submit" type="submit" disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg font-semibold">
                {loading ? 'Logging in…' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mike&apos;s Dashboard</h1>
            <p className="text-xs text-gray-500">Mini Mike Mowing</p>
          </div>
          <Button onClick={logout} variant="outline" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="today" className="w-full">
          <TabsList className="grid grid-cols-3 w-full" data-testid="admin-tabs">
            <TabsTrigger value="today" data-testid="tab-today">
              <Calendar className="h-4 w-4 mr-1 md:inline hidden" /> Today
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">
              <UsersIcon className="h-4 w-4 mr-1 md:inline hidden" /> Customers
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-1 md:inline hidden" /> Analytics
            </TabsTrigger>
          </TabsList>
          <TabsContent value="today" className="mt-4"><TodayView /></TabsContent>
          <TabsContent value="customers" className="mt-4"><CustomersView /></TabsContent>
          <TabsContent value="analytics" className="mt-4"><AnalyticsView /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
